import {z} from 'zod';

import {omneticApi} from '@dms/api/base';

import {
  CreateDataQueryApiResponse,
  CreateDataQueryApiResponseSchema,
  CreateDataQueryApiArg,
  GridTotalRowsResponseBody,
  ApplySharedPresetApiArg,
  ApplySharedPresetApiResponse,
  DeleteVirtualPresetApiArg,
  GetDataByDataQueryApiArg,
  GetDataByDataQueryApiResponse,
  ListDataGridApiArg,
  ListDataGridResponseBody,
  SetActivePresetApiArg,
  ApplySharedPresetApiArgSchema,
  ApplySharedPresetApiResponseSchema,
  DeleteVirtualPresetApiArgSchema,
  GridTotalRowsResponseBodySchema,
  GetDataByDataQueryApiArgSchema,
  GetDataByDataQueryApiResponseSchema,
  ListDataGridResponseBodySchema,
  SetActivePresetApiArgSchema,
} from './types';

export const datagridApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getGridCodesList: build.query<string[], void>({
      query: () => ({
        url: `/dms/v5/data-grid`,
      }),
      extraOptions: {
        responseSchema: z.array(z.string()),
      },
    }),
    getDataGridList: build.query<ListDataGridResponseBody, ListDataGridApiArg>({
      query: (queryArg) => ({
        url: `/dms/v6/data-grid`,
        params: {external: queryArg?.external},
      }),
      extraOptions: {
        responseSchema: ListDataGridResponseBodySchema,
      },
    }),
    setActivePreset: build.mutation<void, SetActivePresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v6/data-grid/${queryArg.code}/preset/active`,
        method: 'PUT',
        body: queryArg.setActivePresetRequestBody,
        params: {instance: queryArg.instance},
      }),
      extraOptions: {
        requestSchema: SetActivePresetApiArgSchema,
      },
    }),
    deleteVirtualPreset: build.mutation<void, DeleteVirtualPresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v6/data-grid/${queryArg.code}/virtual-preset`,
        method: 'DELETE',
        params: {instance: queryArg.instance},
      }),
      extraOptions: {
        requestSchema: DeleteVirtualPresetApiArgSchema,
      },
    }),
    createDataQuery: build.query<CreateDataQueryApiResponse, CreateDataQueryApiArg>({
      query: (queryArg) => ({
        url: `/dms/v6/data-grid/${queryArg.code}/data-query`,
        method: 'POST',
        body: queryArg.createDataQueryRequestBody,
      }),
      transformResponse: (response) => CreateDataQueryApiResponseSchema.parse(response),
      extraOptions: {
        responseSchema: CreateDataQueryApiResponseSchema,
      },
    }),
    getGridTotalRows: build.query<number, {code: string; dataQueryId: string}>({
      query: (queryArg) => ({
        url: `/dms/v6/data-grid/${queryArg.code}/data/${queryArg.dataQueryId}/count`,
      }),
      transformResponse: (response: GridTotalRowsResponseBody) => response.totalRows,
      extraOptions: {
        responseSchema: GridTotalRowsResponseBodySchema,
      },
    }),
    applySharedPreset: build.mutation<ApplySharedPresetApiResponse, ApplySharedPresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v6/data-grid/${queryArg.code}/apply-shared-preset`,
        method: 'PUT',
        body: queryArg.applySharedPresetRequestBody,
        params: {instance: queryArg.instance},
      }),
      extraOptions: {
        requestSchema: ApplySharedPresetApiArgSchema,
        responseSchema: ApplySharedPresetApiResponseSchema,
      },
    }),
    getDataByDataQuery: build.query<GetDataByDataQueryApiResponse, GetDataByDataQueryApiArg>({
      query: (queryArg) => ({
        url: `/dms/v6/data-grid/${queryArg.gridCode}/data/${queryArg.dataQueryId}`,
        method: 'POST',
        body: queryArg.body,
        params: {
          offset: queryArg.offset,
          limit: queryArg.limit,
        },
      }),
      providesTags: (_result, _error, queryArg) => [{type: 'DataGrid', id: queryArg.gridCode}],
      extraOptions: {
        requestSchema: GetDataByDataQueryApiArgSchema,
        responseSchema: GetDataByDataQueryApiResponseSchema,
      },
    }),
    getAllDataByDataQuery: build.query<GetDataByDataQueryApiResponse, GetDataByDataQueryApiArg>({
      queryFn: async (queryArg, _queryApi, _extraOptions, fetchWithBQ) => {
        const rows: GetDataByDataQueryApiResponse['rows'] = [];
        let offset = queryArg.offset;

        while (queryArg.limit > 0) {
          const response = await fetchWithBQ({
            url: `/dms/v6/data-grid/${queryArg.gridCode}/data/${queryArg.dataQueryId}`,
            method: 'POST',
            body: queryArg.body,
            params: {offset, limit: queryArg.limit},
          });

          if (response.error) {
            return {error: response.error};
          }

          const page = GetDataByDataQueryApiResponseSchema.safeParse(response.data);
          if (!page.success) {
            return {error: {status: 'CUSTOM_ERROR', error: page.error.message}};
          }

          rows.push(...page.data.rows);
          if (page.data.rows.length < queryArg.limit) {
            break;
          }

          offset += queryArg.limit;
        }

        return {data: {rows}};
      },
      providesTags: (_result, _error, queryArg) => [{type: 'DataGrid', id: queryArg.gridCode}],
      extraOptions: {
        requestSchema: GetDataByDataQueryApiArgSchema,
        responseSchema: GetDataByDataQueryApiResponseSchema,
      },
    }),
  }),
});

export const {
  useGetGridCodesListQuery,
  useGetDataGridListQuery,
  useCreateDataQueryQuery,
  useLazyCreateDataQueryQuery,
  useGetGridTotalRowsQuery,
  useApplySharedPresetMutation,
  useSetActivePresetMutation,
  useDeleteVirtualPresetMutation,
  useGetDataByDataQueryQuery,
  useGetAllDataByDataQueryQuery,
  useLazyGetDataByDataQueryQuery,
} = datagridApi;
